import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import MainLayout from "../layout/MainLayout";
import {
    completeCandidateDocuments,
    deleteCandidatePackageDocument,
    generateCandidateDocumentsInvite,
    getCandidateEmploymentRequisites,
    listCandidateDocuments,
    requestMoreCandidateDocuments,
    returnCandidateFromDocuments,
    uploadCandidateDocumentManually,
} from "../services/candidateDocumentsApi";
import { useAlertContext } from "../context/AlertContext";
import { CANDIDATE_DOCUMENT_LABELS, CANDIDATE_DOCUMENT_TYPES } from "../config/candidateDocumentTypes";
import useCandidateAutocomplete from "../hooks/useCandidateAutocomplete";
import {relationVacancyName} from "../utils/candidateMapper";
import { validateCandidateDocumentFile } from "../utils/candidateDocumentsDraft";
import CandidateEmploymentRequisites from "../components/candidates/CandidateEmploymentRequisites";
import { restoreEmployee } from "../services/hrOpsApi";
import { CONTRACT_TYPES, contractTypeLabel } from "../config/contractTypes";
import "./CandidateDocumentsPage.css";

const PACKAGE_FILTERS = [
    { value: "all", label: "Все пакеты" },
    { value: "review", label: "Нужно проверить" },
    { value: "attention", label: "Требуют внимания" },
    { value: "complete", label: "Готовы" },
];

const GENERATION_LABELS = {
    NotSent: "Ожидает отправки на составление",
    Queued: "В очереди на составление",
    Sent: "Документы отправлены на составление договоров",
    MissingDocuments: "Для составления договоров не хватает обязательных документов",
    Completed: "Договоры сформированы",
    CompletedWithWarnings: "Договоры сформированы с замечаниями",
    Dead: "Не удалось обработать документы для составления договоров",
};

function packageTone(item) {
    if (item.contract_generation_status === "Dead") return "danger";
    if (item.status === "incomplete" || ["MissingDocuments", "CompletedWithWarnings"].includes(item.contract_generation_status)) return "warning";
    if (item.status === "complete" || item.contract_generation_status === "Completed") return "success";
    return "info";
}

function packageMatchesFilter(item, filter) {
    if (filter === "review") return item.status === "submitted";
    if (filter === "attention") return item.status === "incomplete" || ["MissingDocuments", "CompletedWithWarnings", "Dead"].includes(item.contract_generation_status);
    if (filter === "complete") return item.status === "complete" && !["MissingDocuments", "Dead"].includes(item.contract_generation_status);
    return true;
}

function CandidateDocumentsInvitePanel({ onManualUpload }) {
    const { showAlert } = useAlertContext();
    const [invite, setInvite] = useState(null);
    const [generating, setGenerating] = useState(false);
    const [contractType, setContractType] = useState("employment");
    const [open, setOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);
    const searchRef = useRef(null);
    const {
        query, setQuery, candidates, selectedCandidate, selectCandidate,
        clearSelection, status, error,
    } = useCandidateAutocomplete({debounceMs: 300, limit: 20});

    useEffect(() => {
        const closeOnOutsideClick = (event) => {
            if (searchRef.current && !searchRef.current.contains(event.target)) setOpen(false);
        };
        document.addEventListener("mousedown", closeOnOutsideClick);
        return () => document.removeEventListener("mousedown", closeOnOutsideClick);
    }, []);

    useEffect(() => {
        setActiveIndex(candidates.length ? 0 : -1);
    }, [candidates]);

    const chooseCandidate = (candidate) => {
        selectCandidate(candidate);
        setInvite(null);
        setOpen(false);
    };

    const handleSearchKeyDown = (event) => {
        if (event.key === "ArrowDown") {
            event.preventDefault();
            setOpen(true);
            setActiveIndex(index => Math.min(index + 1, candidates.length - 1));
        } else if (event.key === "ArrowUp") {
            event.preventDefault();
            setActiveIndex(index => Math.max(index - 1, 0));
        } else if (event.key === "Enter" && open && activeIndex >= 0 && candidates[activeIndex]) {
            event.preventDefault();
            chooseCandidate(candidates[activeIndex]);
        } else if (event.key === "Escape") {
            setOpen(false);
        }
    };

    const copyLink = async (url, notify = true) => {
        try {
            if (!navigator.clipboard?.writeText) throw new Error("clipboard unavailable");
            await navigator.clipboard.writeText(url);
            if (notify) showAlert("Ссылка скопирована", "success");
            return true;
        } catch (_) {
            if (notify) showAlert("Не удалось скопировать автоматически. Скопируйте ссылку вручную", "warning");
            return false;
        }
    };

    const generate = async event => {
        event.preventDefault();
        if (!selectedCandidate?.id) return;
        setGenerating(true);
        try {
            const result = await generateCandidateDocumentsInvite(Number(selectedCandidate.id), contractType);
            setInvite(result);
            const copied = await copyLink(result.url, false);
            showAlert(
                copied ? "Ссылка создана и скопирована" : "Ссылка создана. Скопируйте её вручную",
                copied ? "success" : "warning",
            );
        } catch (error) {
            showAlert(error.message || "Не удалось создать ссылку", "error");
        } finally {
            setGenerating(false);
        }
    };

    const listVisible = open && !selectedCandidate && (query.trim().length !== 1);

    return <section className="candidate-invite-panel">
        <div className="candidate-invite-heading">
            <div className="candidate-invite-icon" aria-hidden="true">↗</div>
            <div><span>ПЕРСОНАЛЬНАЯ ССЫЛКА</span><h2>Запросить документы у кандидата</h2><p>Начните вводить имя, телефон или email. Результаты появятся автоматически.</p></div>
        </div>
        <form className="candidate-invite-form" onSubmit={generate}>
            <div className="candidate-search" ref={searchRef}>
                <label htmlFor="candidate-documents-search">Кандидат</label>
                <div className={`candidate-search-input ${selectedCandidate ? "is-selected" : ""}`}>
                    <span className="candidate-search-symbol" aria-hidden="true">⌕</span>
                    <input
                        id="candidate-documents-search"
                        role="combobox"
                        aria-label="Поиск кандидата"
                        aria-autocomplete="list"
                        aria-expanded={listVisible}
                        aria-controls="candidate-documents-results"
                        aria-activedescendant={listVisible && activeIndex >= 0 ? `candidate-result-${candidates[activeIndex]?.id}` : undefined}
                        value={query}
                        onChange={event => { setQuery(event.target.value); setInvite(null); setOpen(true); }}
                        onFocus={() => setOpen(true)}
                        onKeyDown={handleSearchKeyDown}
                        placeholder="ФИО, телефон или email"
                        autoComplete="off"
                    />
                    {status === "loading" && <span className="candidate-search-spinner" aria-label="Идёт поиск"/>}
                    {query && status !== "loading" && <button type="button" className="candidate-search-clear" onClick={() => { clearSelection(); setInvite(null); setOpen(true); }} aria-label="Очистить поиск">×</button>}
                </div>
                {!selectedCandidate && query.trim().length === 1 && <p className="candidate-search-hint">Введите ещё один символ для поиска</p>}
                {!selectedCandidate && !query && <p className="candidate-search-hint">Показываем недавних кандидатов. Для поиска достаточно двух символов.</p>}
                {listVisible && <div id="candidate-documents-results" className="candidate-search-results" role="listbox" aria-label="Результаты поиска кандидатов">
                    {status === "loading" && !candidates.length && <div className="candidate-search-state">Ищем кандидатов…</div>}
                    {status === "error" && <div className="candidate-search-state is-error">{error}</div>}
                    {status === "success" && !candidates.length && <div className="candidate-search-state"><strong>Никого не нашли</strong><span>Проверьте написание или попробуйте телефон и email.</span></div>}
                    {candidates.map((candidate, index) => {
                        const vacancy = relationVacancyName(candidate) || candidate.vacancy_name || candidate.position;
                        const contact = candidate.phone_number || candidate.phone || candidate.email || "Контакты не указаны";
                        return <button
                            id={`candidate-result-${candidate.id}`}
                            key={candidate.id}
                            type="button"
                            role="option"
                            aria-selected={index === activeIndex}
                            className={`candidate-search-result ${index === activeIndex ? "is-active" : ""}`}
                            onMouseEnter={() => setActiveIndex(index)}
                            onClick={() => chooseCandidate(candidate)}
                        >
                            <span className="candidate-result-avatar">{String(candidate.full_name || "К").split(/\s+/).slice(0, 2).map(part => part[0]).join("")}</span>
                            <span className="candidate-result-main"><strong>{candidate.full_name || `Кандидат № ${candidate.id}`}</strong><small>{vacancy || "Вакансия не указана"}</small></span>
                            <span className="candidate-result-contact">{contact}</span>
                        </button>;
                    })}
                </div>}
            </div>

            {selectedCandidate ? <div className="candidate-selected-card">
                <span className="candidate-selected-check">✓</span>
                <div><small>Выбран кандидат</small><strong>{selectedCandidate.full_name || `Кандидат № ${selectedCandidate.id}`}</strong><span>{relationVacancyName(selectedCandidate) || selectedCandidate.vacancy_name || selectedCandidate.email || "Можно создать ссылку или собрать пакет вручную"}</span></div>
                <button type="button" onClick={() => { clearSelection(); setInvite(null); setOpen(true); }}>Изменить</button>
            </div> : <div className="candidate-selection-placeholder"><span>1</span><p>Найдите и выберите кандидата</p></div>}

            <div className="candidate-contract-choice">
                <label htmlFor="candidate-contract-type">Какой договор составить после проверки документов</label>
                <select id="candidate-contract-type" value={contractType} onChange={event => setContractType(event.target.value)}>
                    {CONTRACT_TYPES.map(item => <option key={item.value} value={item.value}>{item.label}</option>)}
                </select>
                <p>Этот выбор видит только HR. В форме загрузки кандидата ничего не меняется.</p>
            </div>
            <div className="flex flex-wrap gap-3">
                <button className="candidate-generate-button flex-1" disabled={generating || !selectedCandidate?.id}>{generating ? "Создаём ссылку…" : "Создать и скопировать ссылку"}</button>
                <button
                    type="button"
                    disabled={!selectedCandidate?.id}
                    className="flex-1 rounded-xl border border-slate-300 bg-white px-5 py-3 font-semibold text-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
                    onClick={() => onManualUpload(selectedCandidate)}
                >
                    Собрать пакет вручную
                </button>
            </div>
        </form>
        {invite?.url ? <aside className="mt-5 rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
            <p className="font-semibold text-emerald-900">Персональная ссылка создана</p>
            <p className="mt-1 text-sm text-emerald-800">Выбран: {contractTypeLabel(invite.contract_type)}</p>
            <p className="mt-1 text-sm text-emerald-800">Передайте её выбранному кандидату. Ссылка действует до {new Date(invite.expires_at).toLocaleString("ru-RU")}.</p>
            <div className="mt-3 flex flex-wrap items-center gap-3"><a className="min-w-0 flex-1 break-all text-sm font-medium text-blue-700 underline" href={invite.url} target="_blank" rel="noreferrer">{invite.url}</a><button type="button" className="rounded-xl border border-emerald-300 bg-white px-4 py-2 font-semibold" onClick={() => copyLink(invite.url)}>Скопировать ссылку</button></div>
        </aside> : null}
    </section>;
}

function ManualDocumentDialog({ candidate, onClose, onUploaded }) {
    const { showAlert } = useAlertContext();
    const [kind, setKind] = useState("passport_personal_data");
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [inputKey, setInputKey] = useState(0);
    const [actualResidenceAddress, setActualResidenceAddress] = useState("");
    const [addressLoading, setAddressLoading] = useState(true);

    useEffect(() => {
        let active = true;
        getCandidateEmploymentRequisites(candidate.id)
            .then((data) => active && setActualResidenceAddress(data?.actual_residence_address || ""))
            .catch(() => active && setActualResidenceAddress(""))
            .finally(() => active && setAddressLoading(false));
        return () => { active = false; };
    }, [candidate.id]);

    const selectFile = event => {
        const selected = event.target.files?.[0] || null;
        if (!selected) {
            setFile(null);
            return;
        }
        try {
            validateCandidateDocumentFile(selected);
            setFile(selected);
        } catch (error) {
            setFile(null);
            setInputKey(value => value + 1);
            showAlert(error.message, "error");
        }
    };

    const submit = async event => {
        event.preventDefault();
        if (!candidate?.id || !file) return;
        setUploading(true);
        try {
            await uploadCandidateDocumentManually(
                candidate.id, kind, file, actualResidenceAddress.trim(),
            );
            await onUploaded();
            showAlert("Документ добавлен в пакет кандидата", "success");
            setFile(null);
            setInputKey(value => value + 1);
        } catch (error) {
            showAlert(error.message || "Не удалось добавить документ", "error");
        } finally {
            setUploading(false);
        }
    };

    return <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/55 p-4" role="dialog" aria-modal="true" aria-labelledby="manual-documents-title">
        <section className="w-full max-w-xl rounded-3xl bg-white p-6 shadow-2xl md:p-8">
            <div className="flex items-start justify-between gap-4">
                <div><h2 id="manual-documents-title" className="text-2xl font-bold">Добавить документ вручную</h2><p className="mt-2 text-sm text-slate-500">{candidate.full_name || `Кандидат № ${candidate.id}`}. Добавляйте документы по одному, затем подтвердите полный пакет в карточке.</p></div>
                <button type="button" className="rounded-lg px-3 py-1 text-xl text-slate-500 hover:bg-slate-100" onClick={onClose} aria-label="Закрыть">×</button>
            </div>
            <form className="mt-6 space-y-5" onSubmit={submit}>
                <label className="block text-sm font-semibold">Тип документа
                    <select aria-label="Тип документа" className="mt-2 w-full rounded-xl border border-slate-300 bg-white p-3" value={kind} onChange={event => setKind(event.target.value)}>
                        {CANDIDATE_DOCUMENT_TYPES.map(document => <option key={document.value} value={document.value}>{document.label}</option>)}
                    </select>
                </label>
                <label className="block text-sm font-semibold">Файл
                    <input key={inputKey} aria-label="Файл документа" className="mt-2 block w-full rounded-xl border border-slate-300 p-3 text-sm" type="file" accept=".jpg,.jpeg,.png,.webp,.pdf" onChange={selectFile} />
                </label>
                <label className="block text-sm font-semibold">Фактический адрес проживания *
                    <textarea aria-label="Фактический адрес проживания" required maxLength={2000} rows={3} value={actualResidenceAddress} onChange={event => setActualResidenceAddress(event.target.value)} placeholder="Индекс, регион, город, улица, дом, квартира" className="mt-2 block w-full rounded-xl border border-slate-300 p-3 text-sm" />
                </label>
                <p className="rounded-xl bg-blue-50 p-3 text-sm text-blue-900">Разрешены JPG, PNG, WEBP и PDF до 10 МБ. Загруженный документ появится в пакете сразу после сохранения.</p>
                <div className="flex justify-end gap-3"><button type="button" className="rounded-xl border px-5 py-3 font-semibold" onClick={onClose}>Закрыть</button><button disabled={!file || !actualResidenceAddress.trim() || uploading || addressLoading} className="rounded-xl bg-slate-900 px-5 py-3 font-semibold text-white disabled:bg-slate-300">{uploading ? "Загрузка…" : addressLoading ? "Загрузка адреса…" : "Добавить документ"}</button></div>
            </form>
        </section>
    </div>;
}

function ManualRequisitesDialog({ item, onClose, onSaved }) {
    const candidate = item.candidate;
    const canRegenerate = item.status === "complete" && item.contract_access?.state === "available";
    return <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/55 p-4" role="dialog" aria-modal="true" aria-labelledby="manual-requisites-title">
        <section className="max-h-[94vh] w-full max-w-4xl overflow-y-auto rounded-3xl bg-slate-50 p-6 shadow-2xl md:p-8">
            <div className="mb-5 flex items-start justify-between gap-4">
                <div><h2 id="manual-requisites-title" className="text-2xl font-bold">Ввести реквизиты вручную</h2><p className="mt-2 text-sm text-slate-600">{candidate.full_name || `Кандидат № ${candidate.id}`}. Укажите ИНН и банковские данные — они станут единым источником для заполнения договоров.</p></div>
                <button type="button" className="rounded-lg px-3 py-1 text-xl text-slate-500 hover:bg-slate-200" onClick={onClose} aria-label="Закрыть">×</button>
            </div>
            <CandidateEmploymentRequisites candidate={candidate} allowRegenerate={canRegenerate} onSaved={onSaved} />
        </section>
    </div>;
}

function CandidateDocumentsPackage({
    item, onComplete, onRequestMore, onManualUpload, onDeleteDocument,
    onReturnCandidate, onEditRequisites, onRestoreEmployee, repeatInvite, pendingAction,
}) {
    const statusLabel = item.status === "complete"
        ? "Полный пакет"
        : item.status === "incomplete"
            ? "Ожидаются недостающие документы"
            : "Ожидает проверки";
    const generationLabel = GENERATION_LABELS[item.contract_generation_status];
    const mayRequestMissing = item.status !== "complete" || item.contract_generation_status === "MissingDocuments";
    const tone = packageTone(item);
    const candidateName = item.candidate.full_name || `Кандидат № ${item.candidate.id}`;
    const contractAccess = item.contract_access || { state: "available" };
    const initials = String(candidateName).split(/\s+/).slice(0, 2).map(part => part[0]).join("");
    return <article className={`candidate-package-card is-${tone}`}>
        <header className="candidate-package-header">
            <div className="candidate-package-person">
                <span className="candidate-package-avatar" aria-hidden="true">{initials}</span>
                <div>
                    <div className="candidate-package-title-row"><h3>{candidateName}</h3><span className={`document-status-pill is-${tone}`}>{statusLabel}</span></div>
                    <p>Пакет получен {new Date(item.submitted_at).toLocaleString("ru-RU")} · {contractTypeLabel(item.contract_type)}</p>
                </div>
            </div>
            <div className="candidate-package-meta" aria-label="Состав пакета"><strong>{item.documents.length}</strong><span>документов</span></div>
        </header>

        {generationLabel ? <div className={`generation-status is-${tone}`}>
            <span className="generation-status-icon" aria-hidden="true">{tone === "danger" ? "!" : tone === "success" ? "✓" : tone === "warning" ? "·" : "↻"}</span>
            <div><strong>{generationLabel}</strong>{item.contract_generation_error ? <p>Причина: {item.contract_generation_error}</p> : <small>Статус обновляется автоматически после обработки пакета.</small>}</div>
        </div> : null}
        {item.revision_reason ? <div className="candidate-revision-note"><strong>{item.status === "incomplete" ? "Что нужно дополнить" : "Последний запрос на дополнение"}</strong><p>{item.revision_reason}</p></div> : null}
        {item.status === "complete" && contractAccess.state !== "available" ? <div className="candidate-revision-note">
            <strong>Почему переход к договорам недоступен</strong>
            <p>{contractAccess.message}</p>
            {contractAccess.state === "employee_inactive" && contractAccess.date_fired ? <small>Дата увольнения: {new Date(contractAccess.date_fired).toLocaleDateString("ru-RU")}</small> : null}
        </div> : null}
        {repeatInvite?.url ? <div className="candidate-repeat-link"><div><strong>Повторная ссылка готова</strong><span>Передайте её кандидату для загрузки недостающих файлов.</span></div><a href={repeatInvite.url} target="_blank" rel="noreferrer">{repeatInvite.url}</a></div> : null}

        <section className="candidate-package-documents" aria-label={`Документы кандидата ${candidateName}`}>
            <div className="candidate-package-section-heading"><div><span>ЗАГРУЖЕННЫЕ ФАЙЛЫ</span><h4>Документы кандидата</h4></div><small>{item.documents.length ? "Нажмите на файл, чтобы открыть оригинал" : "Файлы ещё не загружены"}</small></div>
            {item.documents.length ? <div className="candidate-document-grid">{item.documents.map(document => {
                const label = CANDIDATE_DOCUMENT_LABELS[document.kind] || document.kind;
                const deleting = pendingAction === `delete:${item.id}:${document.id}`;
                const extension = String(document.filename || "").split(".").pop()?.toUpperCase() || "FILE";
                return <div key={document.id || document.key} className="candidate-document-row">
                    <span className="candidate-document-format" aria-hidden="true">{extension.slice(0, 4)}</span>
                    <a href={document.url} target="_blank" rel="noreferrer"><strong>{label}</strong><span title={document.filename}>{document.filename}</span><small>Открыть документ ↗</small></a>
                    <button type="button" aria-label={`Удалить документ ${label}`} disabled={!document.id || deleting} onClick={() => onDeleteDocument(item, document)}>{deleting ? "…" : "×"}</button>
                </div>;
            })}</div> : <div className="candidate-documents-empty"><span aria-hidden="true">□</span><p>В этом пакете пока нет документов</p></div>}
        </section>

        <footer className="candidate-package-actions">
            <div className="candidate-package-primary-actions">
            {mayRequestMissing ? <>
                {item.status === "submitted" ? <button className="document-button is-primary" onClick={() => onComplete(item.id)}>{item.revision_requested_at ? "Повторно отправить на составление" : "Подтвердить полный пакет"}</button> : null}
                <button className="document-button" onClick={() => onManualUpload(item.candidate)}>+ Добавить файл</button>
                <button className="document-button is-warning" onClick={() => onRequestMore(item)}>{item.status === "incomplete" ? "Создать повторную ссылку" : "Запросить недостающие документы"}</button>
            </> : null}
                <button className="document-button" onClick={() => onEditRequisites(item)}>Ввести ИНН и реквизиты</button>
                {item.status === "complete" && contractAccess.state === "available" ? <a href="/employee-contracts" className="document-button is-primary">Перейти к договорам →</a> : null}
                {item.status === "complete" && contractAccess.state === "employee_inactive" ? <button type="button" className="document-button is-primary" disabled={pendingAction === `restore:${contractAccess.employee_id}`} onClick={() => onRestoreEmployee(item)}>{pendingAction === `restore:${contractAccess.employee_id}` ? "Возвращаем…" : "Вернуть в сотрудники"}</button> : null}
                {item.status === "complete" && contractAccess.state === "employee_required" ? <a href="/candidates" className="document-button is-primary">Открыть кандидатов →</a> : null}
            </div>
            <div className="candidate-package-secondary-actions">
                <button
                    type="button"
                    disabled={pendingAction === `return:${item.id}`}
                    className="document-button is-danger-quiet"
                    onClick={() => onReturnCandidate(item)}
                >
                    {pendingAction === `return:${item.id}` ? "Возвращаем…" : "Вернуть в обычные кандидаты"}
                </button>
            </div>
        </footer>
    </article>;
}

function RevisionDialog({ item, onClose, onSubmit, sending }) {
    const uploaded = new Set(item.uploaded_kinds || []);
    const available = CANDIDATE_DOCUMENT_TYPES.filter(({ value }) => value !== "other");
    const suggested = item.contract_generation_missing_document_types?.length
        ? item.contract_generation_missing_document_types
        : item.suggested_missing_kinds || [];
    const [selected, setSelected] = useState(suggested);
    const [sendToHh, setSendToHh] = useState(true);
    const selectedLabels = selected.map(kind => CANDIDATE_DOCUMENT_LABELS[kind] || kind);
    const defaultMessage = `Здравствуйте, ${item.candidate.full_name || "кандидат"}!\n\nМы проверили документы для трудоустройства. Пожалуйста, дополните пакет:\n${selectedLabels.map(label => `— ${label}`).join("\n")}\n\nРанее загруженные документы сохранены. Добавьте только недостающие документы по персональной ссылке.`;
    const [message, setMessage] = useState(defaultMessage);
    const toggle = kind => {
        const next = selected.includes(kind) ? selected.filter(value => value !== kind) : [...selected, kind];
        setSelected(next);
        const labels = next.map(value => CANDIDATE_DOCUMENT_LABELS[value] || value);
        setMessage(`Здравствуйте, ${item.candidate.full_name || "кандидат"}!\n\nМы проверили документы для трудоустройства. Пожалуйста, дополните пакет:\n${labels.map(label => `— ${label}`).join("\n")}\n\nРанее загруженные документы сохранены. Добавьте только недостающие документы по персональной ссылке.`);
    };
    const submit = () => {
        if (!selected.length) return;
        onSubmit({
            reason: selectedLabels.join(", "),
            missing_kinds: selected,
            send_to_hh: sendToHh,
            message: message.trim(),
        });
    };
    return <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/55 p-4" role="dialog" aria-modal="true" aria-labelledby="revision-dialog-title">
        <section className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-3xl bg-white p-6 shadow-2xl md:p-8">
            <div className="flex items-start justify-between gap-4"><div><h2 id="revision-dialog-title" className="text-2xl font-bold">Неполный пакет документов</h2><p className="mt-2 text-sm text-slate-500">Система отметила документы, которых нет в загруженном пакете. Проверьте список перед отправкой.</p></div><button type="button" className="rounded-lg px-3 py-1 text-xl text-slate-500 hover:bg-slate-100" onClick={onClose} aria-label="Закрыть">×</button></div>
            <div className="mt-5 grid gap-2 sm:grid-cols-2">{available.map(document => <label key={document.value} className="flex cursor-pointer items-start gap-3 rounded-xl border p-3 hover:bg-slate-50"><input type="checkbox" className="mt-1" checked={selected.includes(document.value)} onChange={() => toggle(document.value)} /><span><span className="flex flex-wrap items-center gap-2"><b className="block text-sm">{document.label}</b>{uploaded.has(document.value) ? <small className="rounded-full bg-emerald-100 px-2 py-0.5 font-semibold text-emerald-800">уже загружен</small> : <small className="rounded-full bg-amber-100 px-2 py-0.5 font-semibold text-amber-900">не найден</small>}</span>{document.hint ? <small className="mt-1 block text-slate-500">{document.hint}</small> : null}</span></label>)}</div>
            {!suggested.length ? <p className="mt-5 rounded-xl bg-amber-50 p-4 text-sm text-amber-900">Все основные типы документов уже присутствуют. Если файл нечитаемый или в нём не хватает страниц, отметьте его для повторной загрузки.</p> : null}
            <label className="mt-6 block text-sm font-semibold" htmlFor="revision-message">Текст сообщения кандидату</label>
            <textarea id="revision-message" rows="9" className="mt-2 w-full rounded-2xl border border-slate-300 p-4 text-sm leading-6 outline-none focus:border-amber-500" value={message} onChange={event => setMessage(event.target.value)} />
            <p className="mt-2 text-xs text-slate-500">Персональная ссылка будет автоматически добавлена в конец сообщения.</p>
            <label className="mt-5 flex items-start gap-3 rounded-2xl bg-slate-50 p-4"><input type="checkbox" className="mt-1" checked={sendToHh} onChange={event => setSendToHh(event.target.checked)} /><span><b className="block">Направить сообщение кандидату повторно через hh.ru</b><small className="text-slate-500">Если отключить, ссылка только создастся и её можно будет отправить вручную.</small></span></label>
            <div className="mt-6 flex flex-wrap justify-end gap-3"><button type="button" className="rounded-xl border px-5 py-3 font-semibold" onClick={onClose}>Нет, отменить</button><button type="button" disabled={sending || !selected.length || !message.trim()} className="rounded-xl bg-amber-400 px-5 py-3 font-bold text-slate-950 disabled:bg-slate-200 disabled:text-slate-500" onClick={submit}>{sending ? "Отправляем…" : sendToHh ? "Да, создать и отправить" : "Создать ссылку"}</button></div>
        </section>
    </div>;
}

export default function CandidateDocumentsPage() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [repeatInvites, setRepeatInvites] = useState({});
    const [revisionItem, setRevisionItem] = useState(null);
    const [revisionSending, setRevisionSending] = useState(false);
    const [manualCandidate, setManualCandidate] = useState(null);
    const [requisitesItem, setRequisitesItem] = useState(null);
    const [pendingAction, setPendingAction] = useState("");
    const [packageQuery, setPackageQuery] = useState("");
    const [packageFilter, setPackageFilter] = useState("all");
    const { showAlert } = useAlertContext();
    const load = useCallback(async () => {
        try { setLoading(true); setItems(await listCandidateDocuments()); }
        catch (error) { showAlert(error.message, "error"); }
        finally { setLoading(false); }
    }, [showAlert]);
    useEffect(() => { load(); }, [load]);
    const complete = async id => {
        if (!window.confirm("Подтвердить, что все необходимые документы на месте?")) return;
        try { await completeCandidateDocuments(id); await load(); showAlert("Установлен статус «Полный пакет документов»", "success"); }
        catch (error) { showAlert(error.message, "error"); }
    };
    const requestMore = async payload => {
        if (!revisionItem) return;
        setRevisionSending(true);
        try {
            const result = await requestMoreCandidateDocuments(revisionItem.id, payload);
            setRepeatInvites(current => ({ ...current, [revisionItem.id]: result }));
            await load();
            let copied = false;
            try {
                await navigator.clipboard?.writeText(result.url);
                copied = Boolean(navigator.clipboard?.writeText);
            } catch (_) { /* Ссылка останется доступна в карточке пакета. */ }
            const hhSent = result.delivery === "sent";
            showAlert(hhSent ? "Повторная ссылка отправлена кандидату через hh.ru" : copied ? "Повторная ссылка создана и скопирована" : result.delivery === "failed" ? "Ссылка создана, но hh.ru не принял сообщение. Отправьте её вручную" : "Повторная ссылка создана. Скопируйте её из карточки", hhSent || copied ? "success" : "warning");
            setRevisionItem(null);
        } catch (error) {
            showAlert(error.message || "Не удалось создать повторную ссылку", "error");
        } finally {
            setRevisionSending(false);
        }
    };
    const deleteDocument = async (item, document) => {
        const label = CANDIDATE_DOCUMENT_LABELS[document.kind] || document.kind || "документ";
        if (!window.confirm(`Удалить «${label}» (${document.filename})? После удаления пакет потребуется подтвердить повторно.`)) return;
        const action = `delete:${item.id}:${document.id}`;
        setPendingAction(action);
        try {
            await deleteCandidatePackageDocument(item.id, document.id);
            await load();
            showAlert("Документ удалён. Проверьте пакет и подтвердите его повторно", "success");
        } catch (error) {
            showAlert(error.message || "Не удалось удалить документ", "error");
        } finally {
            setPendingAction("");
        }
    };
    const returnCandidate = async item => {
        if (!window.confirm(`Вернуть ${item.candidate.full_name || "кандидата"} в обычные кандидаты? Статус оффера будет заменён на «На рассмотрении».`)) return;
        const action = `return:${item.id}`;
        setPendingAction(action);
        try {
            await returnCandidateFromDocuments(item.id);
            await load();
            showAlert("Кандидат возвращён в общий список со статусом «На рассмотрении»", "success");
        } catch (error) {
            showAlert(error.message || "Не удалось вернуть кандидата", "error");
        } finally {
            setPendingAction("");
        }
    };
    const restoreContractEmployee = async item => {
        const employeeId = item.contract_access?.employee_id;
        if (!employeeId) return;
        if (!window.confirm(`Вернуть ${item.candidate.full_name || "сотрудника"} из уволенных и разрешить составление договоров?`)) return;
        const action = `restore:${employeeId}`;
        setPendingAction(action);
        try {
            await restoreEmployee(employeeId);
            await load();
            showAlert("Сотрудник восстановлен. Пакет доступен в разделе договоров", "success");
        } catch (error) {
            showAlert(error.message || "Не удалось вернуть сотрудника", "error");
        } finally {
            setPendingAction("");
        }
    };
    const packageCounts = useMemo(() => ({
        all: items.length,
        review: items.filter(item => packageMatchesFilter(item, "review")).length,
        attention: items.filter(item => packageMatchesFilter(item, "attention")).length,
        complete: items.filter(item => packageMatchesFilter(item, "complete")).length,
    }), [items]);
    const visibleItems = useMemo(() => {
        const normalizedQuery = packageQuery.trim().toLocaleLowerCase("ru-RU");
        return items.filter(item => packageMatchesFilter(item, packageFilter)).filter(item => {
            if (!normalizedQuery) return true;
            const candidate = item.candidate || {};
            return [candidate.full_name, candidate.phone_number, candidate.email]
                .some(value => String(value || "").toLocaleLowerCase("ru-RU").includes(normalizedQuery));
        });
    }, [items, packageFilter, packageQuery]);
    return <MainLayout>
        <div className="documents-page">
        <header className="documents-page-hero">
            <div><span className="documents-eyebrow">РАБОТА С ДОКУМЕНТАМИ</span><h1>Документы кандидатов</h1><p>Запрашивайте файлы после оффера, проверяйте комплектность и передавайте готовый пакет на составление договоров.</p></div>
            <a href="/employee-contracts" className="documents-hero-link">Открыть договоры <span>→</span></a>
        </header>
        <div className="documents-intake-workspace">
            <aside className="documents-workflow-card"><div><span className="documents-eyebrow">ПОРЯДОК РАБОТЫ</span><h2>От запроса до договора</h2><p>Три шага, чтобы ничего не потерять при оформлении.</p></div><ol className="documents-workflow" aria-label="Этапы работы с документами">
                <li><span>1</span><div><strong>Запросить</strong><small>Ссылка кандидату или ручная загрузка</small></div></li>
                <li><span>2</span><div><strong>Проверить</strong><small>Состав и качество документов</small></div></li>
                <li><span>3</span><div><strong>Сформировать</strong><small>Передать полный пакет в договоры</small></div></li>
            </ol></aside>
            <CandidateDocumentsInvitePanel onManualUpload={setManualCandidate} />
        </div>
        <section className="documents-overview" aria-label="Сводка по пакетам документов">
            <div><span aria-hidden="true">Σ</span><p>Все пакеты<strong>{packageCounts.all}</strong></p></div>
            <div><span className="is-review" aria-hidden="true">⌕</span><p>Нужно проверить<strong>{packageCounts.review}</strong></p></div>
            <div><span className="is-attention" aria-hidden="true">!</span><p>Требуют внимания<strong>{packageCounts.attention}</strong></p></div>
            <div><span className="is-ready" aria-hidden="true">✓</span><p>Готовы<strong>{packageCounts.complete}</strong></p></div>
        </section>
        <section className="candidate-packages-section">
            <div className="candidate-packages-heading"><div><span className="documents-eyebrow">ВХОДЯЩИЕ</span><h2>Полученные пакеты</h2><p>Проверяйте новые документы и быстро находите пакеты, требующие действий.</p></div><span className="candidate-packages-total">{items.length} всего</span></div>
            <div className="candidate-package-toolbar">
                <label className="document-search"><span aria-hidden="true">⌕</span><input aria-label="Поиск по пакетам документов" value={packageQuery} onChange={event => setPackageQuery(event.target.value)} placeholder="Найти кандидата по ФИО, телефону или email" />{packageQuery ? <button type="button" onClick={() => setPackageQuery("")} aria-label="Очистить поиск пакетов">×</button> : null}</label>
                <div className="document-filter-tabs" role="group" aria-label="Фильтр пакетов">{PACKAGE_FILTERS.map(filter => <button type="button" key={filter.value} className={packageFilter === filter.value ? "is-active" : ""} onClick={() => setPackageFilter(filter.value)}>{filter.label}<span>{packageCounts[filter.value]}</span></button>)}</div>
            </div>
            {loading ? <div className="documents-loading-state"><span/><p>Загружаем пакеты документов…</p></div> : null}
            {!loading && !items.length ? <div className="documents-empty-state"><span aria-hidden="true">□</span><h3>Пакетов пока нет</h3><p>Создайте персональную ссылку выше или соберите первый пакет вручную.</p></div> : null}
            {!loading && items.length > 0 && !visibleItems.length ? <div className="documents-empty-state is-compact"><h3>Ничего не найдено</h3><p>Измените поисковый запрос или выберите другой фильтр.</p></div> : null}
            <div className="candidate-package-list">{visibleItems.map(item => <CandidateDocumentsPackage
                key={item.id}
                item={item}
                onComplete={complete}
                onRequestMore={setRevisionItem}
                onManualUpload={setManualCandidate}
                onDeleteDocument={deleteDocument}
                onReturnCandidate={returnCandidate}
                onEditRequisites={setRequisitesItem}
                onRestoreEmployee={restoreContractEmployee}
                repeatInvite={repeatInvites[item.id]}
                pendingAction={pendingAction}
            />)}</div>
        </section>
        {revisionItem ? <RevisionDialog item={revisionItem} onClose={() => setRevisionItem(null)} onSubmit={requestMore} sending={revisionSending} /> : null}
        {manualCandidate ? <ManualDocumentDialog candidate={manualCandidate} onClose={() => setManualCandidate(null)} onUploaded={load} /> : null}
        {requisitesItem ? <ManualRequisitesDialog item={requisitesItem} onClose={() => setRequisitesItem(null)} onSaved={() => { setRequisitesItem(null); load(); }} /> : null}
        </div>
    </MainLayout>;
}
